import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation/Navigation';  // Add this line
import AdminSideNav from '../components/AdminSideNav/AdminSideNav';
import AdminGalleryManagement from './AdminGalleryManagement';
import AdminBannerManagement from './AdminBannerManagement';
import AdminClassesManagement from './AdminClassesManagement';
import AdminCommunityManagement from './AdminCommunityManagement';

import './Dashboard.css';
import './Dashboard.css';
import { apiCall, API_URL } from '../utils/api';

const AdminDashboard = () => {

  //  <div className="admin-dashboard">
  //     <Navigation />
  //   </div> 
  const [isAdmin, setIsAdmin] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [studios, setStudios] = useState([]);
  const [studentAssignments, setStudentAssignments] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [bookingSubmissions, setBookingSubmissions] = useState([]);
  const [teacherApplications, setTeacherApplications] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('trainers');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [allTrainers, setAllTrainers] = useState([]);
  const [trainerSearch, setTrainerSearch] = useState('');
  const [showTeacherPasswords, setShowTeacherPasswords] = useState({});
  const [showUserPasswords, setShowUserPasswords] = useState({});
  const [assignmentModal, setAssignmentModal] = useState({ open: false, studentId: null, selectedAssignTo: '', selectedAssignToType: '' });
  const [studentSearch, setStudentSearch] = useState('');
  const [popup, setPopup] = useState({ open: false, title: '', message: '' });
  const navigate = useNavigate();

  const closePopup = () => {
    setPopup({ open: false, title: '', message: '' });
  };



  const openCertificate = (entity) => {
    if (!entity) return;
    const file = entity.certificateFile;

    if (file) {
      // Check if it's a full URL or relative path
      const url = file.startsWith('http') ? file : `${API_URL.replace('/api', '')}/${file}`;
      window.open(url, '_blank');
    } else {
      setPopup({ open: true, title: 'Document not found', message: 'No document available for this record.' });
    }
  };

  useEffect(() => {
    // Add body class for navigation adjustment
    document.body.classList.add('admin-dashboard-active');

    // Check if user is admin
    const userData = localStorage.getItem('userData');
    if (!userData) {
      navigate('/login');
      return;
    }

    const user = JSON.parse(userData);
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }

    setIsAdmin(true);
    setAdminUser(user);

    // Load Data from API
    const fetchDashboardData = async () => {
      try {
        // 1. Fetch Users by Role
        const teachersData = await apiCall('/users?role=teacher');
        setTeachers(teachersData);

        const usersData = await apiCall('/users?role=user');
        setUsers(usersData);

        const institutionsData = await apiCall('/users?role=institution');
        setInstitutions(institutionsData);

        const studiosData = await apiCall('/users?role=studio');
        setStudios(studiosData);

        // 2. Fetch Other Data
        const bookingsData = await apiCall('/bookings');
        setBookingSubmissions(bookingsData);

        const feedbackData = await apiCall('/feedback');
        setFeedbacks(feedbackData);

        const contactsData = await apiCall('/contact');
        setContactMessages(contactsData);

        const partnersData = await apiCall('/partner-applications');
        setPartnerApplications(partnersData);

        const applicationsData = await apiCall('/teacher-applications');
        setTeacherApplications(applicationsData);

        // Load trainers from the /trainers API
        const trainersData = await apiCall('/trainers');
        setAllTrainers(trainersData);

      } catch (error) {
        console.error("Failed to load dashboard data", error);
        // setPopup({ open: true, title: 'Error', message: 'Failed to load dashboard data' });
      }
    };

    fetchDashboardData();

    // Cleanup function to remove body class
    return () => {
      document.body.classList.remove('admin-dashboard-active');
    };
  }, [navigate]);

  // Refresh data when switching tabs - simplified to just re-fetch or rely on state
  useEffect(() => {
    // Optional: could re-fetch here if needed
  }, [activeTab]);

  // Data is now loaded from database in the initial useEffect
  // No need for localStorage refresh

  const handleLogout = () => {
    localStorage.removeItem('userData');
    navigate('/');
  };

  const handleAddTeacher = () => {
    setFormData({});
    setEditingId(null);
    setShowForm(true);
  };

  const handleEditTeacher = (teacher) => {
    // Map joinDate to joiningDate for form compatibility
    const data = { ...teacher, joiningDate: teacher.joinDate || teacher.joiningDate };
    setFormData(data);
    setEditingId(teacher._id || teacher.id);
    setShowForm(true);
  };

  const handleDeleteTeacher = async (id) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      try {
        await apiCall(`/users/${id}`, 'DELETE');
        const teachersData = await apiCall('/users?role=teacher');
        setTeachers(teachersData);
      } catch (error) {
        alert('Failed to delete teacher: ' + error.message);
      }
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return 'Password must contain at least one special character (!@#$%^&* etc)';
    }
    return null;
  };

  const handleSaveTeacher = async () => {
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill all required fields (Name, Email, Phone)');
      return;
    }
    if (!editingId && !formData.password) {
      alert('Password is required for new teachers');
      return;
    }

    // Use FormData for compatibility with multer backend
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      // Skip certificateFile if it's not a file object (e.g. existing string URL)
      // But if it is a File object, append it
      if (key === 'certificateFile' && !(formData[key] instanceof File)) {
        return;
      }
      submitData.append(key, formData[key]);
    });

    // Ensure role is teacher
    submitData.append('role', 'teacher');
    if (formData.joiningDate) {
      submitData.append('joinDate', formData.joiningDate);
    }

    try {
      if (editingId) {
        // Pass true for isFileUpload to ensure apiCall doesn't set Content-Type: application/json
        await apiCall(`/users/${editingId}`, 'PUT', submitData, true);
        alert('✅ Teacher updated successfully!');
      } else {
        await apiCall('/users', 'POST', submitData, true);
        alert('✅ Teacher added successfully!');
      }

      // Refresh
      const teachersData = await apiCall('/users?role=teacher');
      setTeachers(teachersData);
      setShowForm(false);
      setFormData({});
      setEditingId(null);
    } catch (error) {
      console.error("Save teacher error:", error);
      alert('Failed to save teacher: ' + error.message);
    }
  };

  const handleAddUser = () => {
    setFormData({});
    setEditingId(null);
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    setFormData(user);
    setEditingId(user._id || user.id); // Support both _id and id
    setShowForm(true);
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiCall(`/users/${id}`, 'DELETE');
        const usersData = await apiCall('/users?role=user');
        setUsers(usersData);
        alert('✅ User deleted successfully!');
      } catch (error) {
        alert('Failed to delete user: ' + error.message);
      }
    }
  };

  const handleSaveUser = async () => {
    // Validation
    if (!formData.name || !formData.email) {
      alert('Please fill name and email');
      return;
    }

    // Password required only for new users
    if (!editingId && !formData.password) {
      alert('Password is required for new users');
      return;
    }

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'certificateFile' && !(formData[key] instanceof File)) return;
      submitData.append(key, formData[key]);
    });
    submitData.append('role', 'user');

    try {
      if (editingId) {
        await apiCall(`/users/${editingId}`, 'PUT', submitData, true);
        alert('✅ User updated successfully!');
      } else {
        await apiCall('/users', 'POST', submitData, true);
        alert('✅ User created successfully!');
      }

      // Refresh
      const usersData = await apiCall('/users?role=user');
      setUsers(usersData);
      setShowForm(false);
      setFormData({});
      setEditingId(null);
    } catch (error) {
      alert('Failed to save user: ' + error.message);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === 'file') {
      setFormData({ ...formData, [name]: files[0] });
    } else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'assignedTeacherId') {
      setFormData({ ...formData, [name]: value ? parseInt(value) : null });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleDeleteBooking = async (id) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await apiCall(`/bookings/${id}`, 'DELETE');
        const bookingsData = await apiCall('/bookings');
        setBookingSubmissions(bookingsData);
      } catch (error) {
        alert('Failed to delete booking: ' + error.message);
      }
    }
  };

  const handleDeleteTeacherApp = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await apiCall(`/teacher-applications/${id}`, 'DELETE');
        const applicationsData = await apiCall('/teacher-applications');
        setTeacherApplications(applicationsData);
      } catch (error) {
        alert('Failed to delete application: ' + error.message);
      }
    }
  };

  // Institution Management Handlers
  const handleEditInstitution = (institution) => {
    setFormData(institution);
    setEditingId(institution._id || institution.id);
    setShowForm(true);
    setActiveTab('edit-institution');
  };

  const handleDeleteInstitution = async (id) => {
    if (window.confirm('Are you sure you want to delete this institution?')) {
      try {
        await apiCall(`/users/${id}`, 'DELETE');
        const institutionsData = await apiCall('/users?role=institution');
        setInstitutions(institutionsData);
      } catch (error) {
        alert('Failed to delete institution: ' + error.message);
      }
    }
  };

  const handleSaveInstitution = async () => {
    if (!formData.institutionName || !formData.email || !formData.phone) {
      alert('Please fill all required fields (Name, Email, Phone)');
      return;
    }
    if (!editingId && !formData.password) {
      alert('Password is required for new institutions');
      return;
    }

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'certificateFile' && !(formData[key] instanceof File)) return;
      submitData.append(key, formData[key]);
    });
    submitData.append('role', 'institution');

    try {
      if (editingId) {
        await apiCall(`/users/${editingId}`, 'PUT', submitData, true);
        alert('✅ Institution updated successfully!');
      } else {
        await apiCall('/users', 'POST', submitData, true);
        alert('✅ Institution created successfully!');
      }

      const institutionsData = await apiCall('/users?role=institution');
      setInstitutions(institutionsData);
      setShowForm(false);
      setFormData({});
      setActiveTab('institutions');
    } catch (error) {
      alert('Failed to save institution: ' + error.message);
    }
  };

  // Studio Management Handlers
  const handleEditStudio = (studio) => {
    setFormData(studio);
    setEditingId(studio._id || studio.id);
    setShowForm(true);
    setActiveTab('edit-studio');
  };

  const handleDeleteStudio = async (id) => {
    if (window.confirm('Are you sure you want to delete this studio?')) {
      try {
        await apiCall(`/users/${id}`, 'DELETE');
        const studiosData = await apiCall('/users?role=studio');
        setStudios(studiosData);
      } catch (error) {
        alert('Failed to delete studio: ' + error.message);
      }
    }
  };

  const handleSaveStudio = async () => {
    if (!formData.studioName || !formData.email || !formData.phone) {
      alert('Please fill all required fields (Name, Email, Phone)');
      return;
    }
    if (!editingId && !formData.password) {
      alert('Password is required for new studios');
      return;
    }

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'certificateFile' && !(formData[key] instanceof File)) return;
      submitData.append(key, formData[key]);
    });
    submitData.append('role', 'studio');

    try {
      if (editingId) {
        await apiCall(`/users/${editingId}`, 'PUT', submitData, true);
        alert('✅ Studio updated successfully!');
      } else {
        await apiCall('/users', 'POST', submitData, true);
        alert('✅ Studio created successfully!');
      }

      const studiosData = await apiCall('/users?role=studio');
      setStudios(studiosData);
      setShowForm(false);
      setFormData({});
      setActiveTab('studios');
    } catch (error) {
      alert('Failed to save studio: ' + error.message);
    }
  };

  // Trainer Management Handlers - UPDATED TO USE API
  const loadTrainers = async () => {
    try {
      const trainers = await apiCall('/trainers');
      setAllTrainers(trainers);
      return trainers;
    } catch (error) {
      console.error("Failed to load trainers", error);
      // Fallback
      setAllTrainers([]);
    }
  };

  const handleTrainerSubmit = async () => {
    if (!formData.name || !formData.email) {
      alert('Name and Email are required');
      return;
    }

    // Create FormData for file upload
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key]);
    });

    try {
      let isFile = false;
      if (formData.photo instanceof File) {
        isFile = true;
      }

      // If no file but other updates, we still might need FormData if using same endpoint, 
      // but apiCall helper handles parsing.
      // However, typical pattern is to send FormData if we have files OR if the endpoint expects multipart always.
      // My trainer routes use upload.single('photo'), so it likely expects multipart or at least tolerates it.
      // But for simplicty, let's always use FormData for Trainers.

      const payload = submitData;

      if (editingId) {
        await apiCall(`/trainers/${editingId}`, 'PUT', payload, true); // true for fileUpload
        alert('Trainer updated successfully!');
      } else {
        await apiCall('/trainers', 'POST', payload, true);
        alert('Trainer added successfully!');
      }

      await loadTrainers();
      setShowForm(false);
      setFormData({});
      setEditingId(null);
    } catch (error) {
      alert('Failed to save trainer: ' + error.message);
    }
  };


  const handleEditTrainer = (trainer) => {
    setFormData(trainer);
    setEditingId(trainer._id || trainer.id);
    setShowForm(true);
  };


  const handleDeleteTrainer = async (id) => {
    if (window.confirm('Are you sure you want to delete this trainer?')) {
      try {
        await apiCall(`/trainers/${id}`, 'DELETE');
        await loadTrainers();
        alert('Trainer deleted successfully!');
      } catch (error) {
        alert('Failed to delete trainer: ' + error.message);
      }
    }
  };

  // Load trainers on mount and when tab changes
  useEffect(() => {
    if (activeTab === 'trainers') {
      loadTrainers();
    }
  }, [activeTab]);

  // Add test data (for development/testing)
  const addTestData = () => {
    const testBooking = {
      id: Date.now(),
      name: 'Test User',
      email: 'test@example.com',
      phone: '9876543210',
      selectedClass: 'Beginner Yoga',
      preferredTime: '6:00 AM',
      submittedAt: new Date().toISOString()
    };

    const testApp = {
      id: Date.now() + 1,
      name: 'Test Teacher',
      email: 'teacher@example.com',
      phone: '9876543215',
      experience: '5 years',
      certifications: 'RYT 200',
      message: 'I am interested in teaching yoga',
      submittedAt: new Date().toISOString()
    };


  };

  if (!isAdmin) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <main className="main-content">
        <header className="dashboard-header" style={{ padding: '15px' }}>
          <div className="dashboard-header-content">
            <div>
              <h1>Welcome, {adminUser?.email || 'Admin'}</h1>
              <p>Manage teachers, students, and track platform activity</p>
            </div>
            <div className="header-buttons">

            </div>
          </div>
        </header>

        {/* Admin Side Navigation - Desktop Only */}
        <div className="desktop-only">
          <AdminSideNav
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            counts={{
              teachers: teachers.length,
              users: users.length,
              institutions: institutions.length,
              studios: studios.length,
              feedbacks: feedbacks.length,
              bookingSubmissions: bookingSubmissions.length,
              contactMessages: contactMessages.length,
              partnerApplications: partnerApplications.length,
              trainers: allTrainers.length,
              teacherApplications: teacherApplications.length
            }}
          />
        </div>

        {/* Horizontal Tabs - Mobile Only */}
        <div className="mobile-only">
          <div className="admin-tabs">
            <button
              className={`tab-btn ${activeTab === 'trainers' ? 'active' : ''}`}
              onClick={() => setActiveTab('trainers')}
            >
              🧘‍♂️ Yoga Trainers ({allTrainers.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'partner_apps' ? 'active' : ''}`}
              onClick={() => setActiveTab('partner_apps')}
            >
              🤝 Partner Apps ({partnerApplications.length})
            </button>
          </div>
        </div>


        {/* Main Content */}
        <div className="admin-main-content">

          {/* Teachers Section */}
          {activeTab === 'teachers' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Teachers Management</h2>
                <button onClick={handleAddTeacher} className="btn btn-primary">
                  ➕ Add Teacher
                </button>
              </div>

              {showForm && activeTab === 'teachers' && (
                <div className="form-card card">
                  <div className="card-header">
                    <h3>{editingId ? 'Edit Teacher' : 'Add New Teacher'}</h3>
                  </div>
                  <div className="card-content">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleFormChange}
                          placeholder="Teacher name"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleFormChange}
                          placeholder="Email address"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password || ''}
                          onChange={handleFormChange}
                          placeholder={editingId ? 'Leave empty to keep current password' : 'Create password'}
                          required={!editingId}
                        />
                      </div>
                      <div className="form-group">
                        <label>Joining Date</label>
                        <input
                          type="date"
                          name="joiningDate"
                          value={formData.joiningDate ? formData.joiningDate.split('T')[0] : new Date().toISOString().split('T')[0]}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone || ''}
                          onChange={handleFormChange}
                          placeholder="Phone number"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Specialization</label>
                        <input
                          type="text"
                          name="specialization"
                          value={formData.specialization || ''}
                          onChange={handleFormChange}
                          placeholder="e.g., Hatha Yoga"
                        />
                      </div>
                      <div className="form-group">
                        <label>Experience</label>
                        <input
                          type="text"
                          name="experience"
                          value={formData.experience || ''}
                          onChange={handleFormChange}
                          placeholder="e.g., 8 years"
                        />
                      </div>
                      <div className="form-group">
                        <label>Qualification</label>
                        <input
                          type="text"
                          name="qualification"
                          value={formData.qualification || ''}
                          onChange={handleFormChange}
                          placeholder="e.g., RYT 500 Certified"
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          name="status"
                          value={formData.status || 'Active'}
                          onChange={handleFormChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Suspended">Suspended</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button onClick={handleSaveTeacher} className="btn btn-primary">
                        Save
                      </button>
                      <button onClick={() => setShowForm(false)} className="btn btn-secondary">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Phone</th>
                      <th>Joining Date</th>
                      <th>Specialization</th>
                      <th>Experience</th>
                      <th>Qualification</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map(teacher => {
                      const showPassword = showTeacherPasswords[teacher.id];
                      const passwordDisplay = showPassword ? teacher.password : teacher.password?.substring(0, 8) || '●●●●●●●●';
                      return (
                        <tr key={teacher.id}>
                          <td><strong>{teacher.name}</strong></td>
                          <td>{teacher.email}</td>
                          <td style={{ minWidth: '150px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <code style={{ backgroundColor: '#f0f0f0', padding: '4px 8px', borderRadius: '3px', fontFamily: 'monospace' }}>
                                {showPassword ? teacher.password : '●●●●●●●●'}
                              </code>
                              <button
                                onClick={() => setShowTeacherPasswords({ ...showTeacherPasswords, [teacher.id]: !showPassword })}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  padding: '2px 4px'
                                }}
                                title={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                              </button>
                            </div>
                          </td>
                          <td><a href={`tel:${teacher.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{teacher.phone}</a></td>
                          <td>{teacher.joinDate ? new Date(teacher.joinDate).toLocaleDateString() : (teacher.joiningDate ? new Date(teacher.joiningDate).toLocaleDateString() : 'N/A')}</td>
                          <td>{teacher.specialization}</td>
                          <td>{teacher.experience}</td>
                          <td>{teacher.qualification || 'N/A'}</td>
                          <td>
                            <span className={`badge ${teacher.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>
                              {teacher.status}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => handleDeleteTeacher(teacher.id)}
                                className="btn btn-sm btn-danger"
                              >
                                🗑️ Delete
                              </button>
                              <button
                                onClick={() => handleEditTeacher(teacher)}
                                className="btn btn-sm btn-primary"
                              >
                                ✏️ Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Users Section */}
          {activeTab === 'users' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Users Management</h2>
                <button onClick={handleAddUser} className="btn btn-primary">
                  ➕ Add User
                </button>
              </div>

              {showForm && activeTab === 'users' && (
                <div className="form-card card">
                  <div className="card-header">
                    <h3>{editingId ? 'Edit User' : 'Add New User'}</h3>
                  </div>
                  <div className="card-content">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleFormChange}
                          placeholder="User name"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleFormChange}
                          placeholder="Email address"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password || ''}
                          onChange={handleFormChange}
                          placeholder={editingId ? 'Leave empty to keep current password' : 'Create password'}
                          required={!editingId}
                        />
                      </div>
                      <div className="form-group">
                        <label>Join Date</label>
                        <input
                          type="date"
                          name="joinDate"
                          value={formData.joinDate ? formData.joinDate.split('T')[0] : new Date().toISOString().split('T')[0]}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Program</label>
                        <input
                          type="text"
                          name="program"
                          value={formData.program || ''}
                          onChange={handleFormChange}
                          placeholder="e.g., Beginner Yoga"
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          name="status"
                          value={formData.status || 'Active'}
                          onChange={handleFormChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Suspended">Suspended</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Assign Teacher</label>
                        <select
                          name="assignedTeacherId"
                          value={formData.assignedTeacherId || ''}
                          onChange={handleFormChange}
                        >
                          <option value="">Select a Teacher</option>
                          {teachers.map(teacher => (
                            <option key={teacher.id} value={teacher.id}>
                              {teacher.name} - {teacher.specialization}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone || ''}
                          onChange={handleFormChange}
                          placeholder="Phone number"
                        />
                      </div>
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city || ''}
                          onChange={handleFormChange}
                          placeholder="City"
                        />
                      </div>
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state || ''}
                          onChange={handleFormChange}
                          placeholder="State"
                        />
                      </div>
                      <div className="form-group">
                        <label>Address</label>
                        <textarea
                          name="address"
                          value={formData.address || ''}
                          onChange={handleFormChange}
                          placeholder="Full address"
                          rows="2"
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button onClick={handleSaveUser} className="btn btn-primary">
                        Save
                      </button>
                      <button onClick={() => setShowForm(false)} className="btn btn-secondary">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Phone</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Address</th>
                      <th>Program</th>
                      <th>Assigned Teacher</th>
                      <th>Join Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => {
                      const userId = user._id || user.id;
                      const assignedTeacher = teachers.find(t => (t._id || t.id) === user.assignedTeacherId?._id || (t._id || t.id) === user.assignedTeacherId);
                      const showPassword = showUserPasswords[userId];
                      return (
                        <tr key={userId}>
                          <td><strong>{user.name}</strong></td>
                          <td>{user.email}</td>
                          <td style={{ minWidth: '150px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <code style={{ backgroundColor: '#f0f0f0', padding: '4px 8px', borderRadius: '3px', fontFamily: 'monospace' }}>
                                {showPassword ? user.password : '●●●●●●●●'}
                              </code>
                              <button
                                onClick={() => setShowUserPasswords({ ...showUserPasswords, [userId]: !showPassword })}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  padding: '2px 4px'
                                }}
                                title={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                              </button>
                            </div>
                          </td>
                          <td><a href={`tel:${user.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{user.phone}</a></td>
                          <td>{user.city || 'N/A'}</td>
                          <td>{user.state || 'N/A'}</td>
                          <td>
                            <div style={{ maxHeight: '100px', overflowY: 'auto', minWidth: '200px' }} title={user.address}>
                              {user.address || 'N/A'}
                            </div>
                          </td>
                          <td>{user.program}</td>
                          <td>
                            {assignedTeacher || user.assignedTeacherId?.name ? (
                              <span className="teacher-badge">
                                👨‍🏫 {assignedTeacher?.name || user.assignedTeacherId?.name}
                              </span>
                            ) : (
                              <span style={{ color: '#999' }}>Not assigned</span>
                            )}
                          </td>
                          <td>{new Date(user.joinDate).toLocaleDateString()}</td>

                          <td>
                            <span className={`badge ${user.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>
                              {user.status}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => handleDeleteUser(userId)}
                                className="btn btn-sm btn-danger"
                              >
                                🗑️ Delete
                              </button>
                              <button
                                onClick={() => handleEditUser(user)}
                                className="btn btn-sm btn-primary"
                              >
                                ✏️ Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Institutions Section */}
          {activeTab === 'institutions' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>🏫 Yoga Institutions Management</h2>
              </div>

              {institutions.length > 0 ? (
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Institution Name</th>
                        <th>Email</th>
                        <th>Password</th>
                        <th>Phone</th>
                        <th>Address</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Postal Code</th>
                        <th>Country</th>
                        <th>Founding Year</th>
                        <th>Charges</th>
                        <th>Documents</th>
                        <th>Students Assigned</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {institutions.map(institution => {
                        const assignedCount = studentAssignments.filter(a => a.assignedTo === institution.id && a.assignedToType === 'institution').length;
                        const showPassword = showUserPasswords[institution.id];
                        return (
                          <tr key={institution.id}>
                            <td><strong>{institution.institutionName}</strong></td>
                            <td>{institution.email}</td>
                            <td style={{ minWidth: '150px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <code style={{ backgroundColor: '#f0f0f0', padding: '4px 8px', borderRadius: '3px', fontFamily: 'monospace' }}>
                                  {showPassword ? institution.password : '●●●●●●●●'}
                                </code>
                                <button
                                  onClick={() => setShowUserPasswords({ ...showUserPasswords, [institution.id]: !showPassword })}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    padding: '2px 4px'
                                  }}
                                  title={showPassword ? 'Hide password' : 'Show password'}
                                >
                                  {showPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                              </div>
                            </td>
                            <td><a href={`tel:${institution.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{institution.phone}</a></td>
                            <td style={{ whiteSpace: 'nowrap', maxWidth: '250px', overflow: 'auto', textOverflow: 'clip' }} title={institution.address}>{institution.address}</td>
                            <td>{institution.city}</td>
                            <td>{institution.state}</td>
                            <td>{institution.postalCode}</td>
                            <td>{institution.country}</td>
                            <td>{institution.foundingYear || 'N/A'}</td>
                            <td>{institution.charges || 'N/A'}</td>
                            <td>
                              {institution.certificateFile || institution.certificateFileId ? (
                                <button
                                  className="btn btn-small btn-info"
                                  onClick={() => openCertificate(institution)}
                                  title={institution.certificateFileName}
                                >
                                  📄 View
                                </button>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td><span className="badge badge-info">{assignedCount}</span></td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  onClick={() => handleEditInstitution(institution)}
                                  className="btn btn-sm btn-primary"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteInstitution(institution.id)}
                                  className="btn btn-sm btn-danger"
                                >
                                  🗑️ Delete
                                </button>
                                <button
                                  onClick={() => setAssignmentModal({ open: true, studentId: null, selectedAssignTo: institution.id, selectedAssignToType: 'institution' })}
                                  className="btn btn-sm btn-success"
                                >
                                  ➕ Assign
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                  <p>No institutions registered yet</p>
                </div>
              )}
            </section>
          )}

          {/* Studios Section */}
          {activeTab === 'studios' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>🧘 Yoga Studios Management</h2>
              </div>

              {studios.length > 0 ? (
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Studio Name</th>
                        <th>Email</th>
                        <th>Password</th>
                        <th>Phone</th>
                        <th>Founding Year</th>
                        <th>Charges</th>
                        <th>Address</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Postal Code</th>
                        <th>Country</th>
                        <th>Documents</th>
                        <th>Students Assigned</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studios.map(studio => {
                        const studioId = studio._id || studio.id;
                        const assignedCount = studentAssignments.filter(a => a.assignedTo === studioId && a.assignedToType === 'studio').length;
                        const showPassword = showUserPasswords[studioId];
                        return (
                          <tr key={studioId}>
                            <td><strong>{studio.studioName}</strong></td>
                            <td>{studio.email}</td>
                            <td style={{ minWidth: '150px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <code style={{ backgroundColor: '#f0f0f0', padding: '4px 8px', borderRadius: '3px', fontFamily: 'monospace' }}>
                                  {showPassword ? studio.password : '●●●●●●●●'}
                                </code>
                                <button
                                  onClick={() => setShowUserPasswords({ ...showUserPasswords, [studioId]: !showPassword })}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    padding: '2px 4px'
                                  }}
                                  title={showPassword ? 'Hide password' : 'Show password'}
                                >
                                  {showPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                              </div>
                            </td>
                            <td><a href={`tel:${studio.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{studio.phone || 'N/A'}</a></td>
                            <td>{studio.foundingYear || '-'}</td>
                            <td>{studio.charges || '-'}</td>
                            <td style={{ whiteSpace: 'nowrap', maxWidth: '250px', overflow: 'auto', textOverflow: 'clip' }} title={studio.address}>{studio.address}</td>
                            <td>{studio.city}</td>
                            <td>{studio.state}</td>
                            <td>{studio.postalCode}</td>
                            <td>{studio.country}</td>
                            <td>
                              {studio.certificateFile || studio.certificateFileId ? (
                                <button
                                  className="btn btn-small btn-info"
                                  onClick={() => openCertificate(studio)}
                                  title={studio.certificateFileName}
                                >
                                  📄 View
                                </button>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td><span className="badge badge-info">{assignedCount}</span></td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  onClick={() => handleEditStudio(studio)}
                                  className="btn btn-sm btn-primary"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteStudio(studioId)}
                                  className="btn btn-sm btn-danger"
                                >
                                  🗑️ Delete
                                </button>
                                <button
                                  onClick={() => setAssignmentModal({ open: true, studentId: null, selectedAssignTo: studio.id, selectedAssignToType: 'studio' })}
                                  className="btn btn-sm btn-success"
                                >
                                  ➕ Assign
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                  <p>No studios registered yet</p>
                </div>
              )}
            </section>
          )}

          {/* Assignment Modal */}
          {assignmentModal.open && (
            <div className="modal-overlay" onClick={() => setAssignmentModal({ ...assignmentModal, open: false })}>
              <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>📚 Assign Student to {assignmentModal.selectedAssignToType === 'institution' ? 'Institution' : 'Studio'}</h3>
                  <button onClick={() => setAssignmentModal({ ...assignmentModal, open: false })} style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '24px' }}>×</button>
                </div>
                <div className="modal-body">
                  <div className="search-section" style={{ marginBottom: '1rem' }}>
                    <input
                      type="text"
                      placeholder="Search by name, city, state, or phone..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <p className="modal-subtitle">Select a student from the list below to assign</p>
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Address</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users
                          .filter(user => {
                            // Filter out already assigned students
                            const isAlreadyAssigned = studentAssignments.some(a =>
                              a.studentId === user.id &&
                              a.assignedToType === assignmentModal.selectedAssignToType
                            );

                            // Apply search filter
                            const searchLower = studentSearch.toLowerCase();
                            const matchesSearch = !studentSearch ||
                              user.name.toLowerCase().includes(searchLower) ||
                              user.email.toLowerCase().includes(searchLower) ||
                              (user.city && user.city.toLowerCase().includes(searchLower)) ||
                              (user.state && user.state.toLowerCase().includes(searchLower)) ||
                              (user.phone && user.phone.includes(studentSearch));

                            return !isAlreadyAssigned && matchesSearch;
                          })
                          .map(user => (
                            <tr
                              key={user.id}
                              className={assignmentModal.studentId === user.id ? 'selected-row' : ''}
                              onClick={() => setAssignmentModal({ ...assignmentModal, studentId: user.id })}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <input
                                  type="radio"
                                  name="studentSelection"
                                  checked={assignmentModal.studentId === user.id}
                                  onChange={() => setAssignmentModal({ ...assignmentModal, studentId: user.id })}
                                />
                              </td>
                              <td><strong>{user.name}</strong></td>
                              <td>{user.email}</td>
                              <td><a href={`tel:${user.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{user.phone || 'N/A'}</a></td>
                              <td>{user.city || 'N/A'}</td>
                              <td>{user.state || 'N/A'}</td>
                              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.address}>{user.address || 'N/A'}</td>
                              <td>
                                <span className={`badge ${user.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>
                                  {user.status || 'Active'}
                                </span>
                              </td>
                              <td>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const newAssignment = {
                                      id: Date.now(),
                                      studentId: user.id,
                                      assignedTo: assignmentModal.selectedAssignTo,
                                      assignedToType: assignmentModal.selectedAssignToType,
                                      assignedAt: new Date().toISOString()
                                    };
                                    const updated = [...studentAssignments, newAssignment];
                                    setStudentAssignments(updated);
                                    try {
                                      await apiCall(`/users/${user.id}`, 'PUT', { assignedTeacherId: assignmentModal.selectedAssignTo });
                                      const usersData = await apiCall('/users?role=user');
                                      setUsers(usersData);
                                      setAssignmentModal({ open: false, studentId: null, selectedAssignTo: '', selectedAssignToType: '' });
                                      setStudentSearch('');
                                      alert('✅ Student assigned successfully!');
                                    } catch (err) {
                                      alert('Failed to assign student: ' + err.message);
                                    }
                                  }}
                                  className="btn btn-sm btn-success"
                                >
                                  ➕ Assign
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {users.filter(user => {
                      const isAlreadyAssigned = studentAssignments.some(a =>
                        a.studentId === user.id &&
                        a.assignedToType === assignmentModal.selectedAssignToType
                      );
                      const searchLower = studentSearch.toLowerCase();
                      const matchesSearch = !studentSearch ||
                        user.name.toLowerCase().includes(searchLower) ||
                        user.email.toLowerCase().includes(searchLower) ||
                        (user.city && user.city.toLowerCase().includes(searchLower)) ||
                        (user.state && user.state.toLowerCase().includes(searchLower)) ||
                        (user.phone && user.phone.includes(studentSearch));
                      return !isAlreadyAssigned && matchesSearch;
                    }).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                          <p>No unassigned students found</p>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Institution Section */}
          {activeTab === 'edit-institution' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>{editingId ? 'Edit Institution' : 'Add New Institution'}</h2>
              </div>

              <div className="form-card card">
                <div className="card-content">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Institution Name</label>
                      <input
                        type="text"
                        name="institutionName"
                        value={formData.institutionName || ''}
                        onChange={handleFormChange}
                        placeholder="Institution name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleFormChange}
                        placeholder="Email address"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Password</label>
                      <label>Password</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password || ''}
                        onChange={handleFormChange}
                        placeholder="Password (leave empty to keep current)"
                        required={!editingId}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleFormChange}
                        placeholder="Phone number"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address || ''}
                        onChange={handleFormChange}
                        placeholder="Street address"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleFormChange}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state || ''}
                        onChange={handleFormChange}
                        placeholder="State"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode || ''}
                        onChange={handleFormChange}
                        placeholder="Postal code"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country || 'India'}
                        onChange={handleFormChange}
                        placeholder="Country"
                      />
                    </div>
                    <div className="form-group">
                      <label>Founding Year</label>
                      <input
                        type="number"
                        name="foundingYear"
                        value={formData.foundingYear || ''}
                        onChange={handleFormChange}
                        placeholder="Founding year"
                      />
                    </div>
                    <div className="form-group">
                      <label>Charges</label>
                      <input
                        type="text"
                        name="charges"
                        value={formData.charges || ''}
                        onChange={handleFormChange}
                        placeholder="Charges"
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button onClick={handleSaveInstitution} className="btn btn-primary">
                      Save
                    </button>
                    <button onClick={() => {
                      setShowForm(false);
                      setFormData({});
                      setEditingId(null);
                      setActiveTab('institutions');
                    }} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Edit Studio Section */}
          {activeTab === 'edit-studio' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>{editingId ? 'Edit Studio' : 'Add New Studio'}</h2>
              </div>

              <div className="form-card card">
                <div className="card-content">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Studio Name</label>
                      <input
                        type="text"
                        name="studioName"
                        value={formData.studioName || ''}
                        onChange={handleFormChange}
                        placeholder="Studio name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleFormChange}
                        placeholder="Email address"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Password</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password || ''}
                        onChange={handleFormChange}
                        placeholder="Password (leave empty to keep current)"
                        required={!editingId}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleFormChange}
                        placeholder="Phone number"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address || ''}
                        onChange={handleFormChange}
                        placeholder="Street address"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleFormChange}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state || ''}
                        onChange={handleFormChange}
                        placeholder="State"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode || ''}
                        onChange={handleFormChange}
                        placeholder="Postal code"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country || 'India'}
                        onChange={handleFormChange}
                        placeholder="Country"
                      />
                    </div>
                    <div className="form-group">
                      <label>Charges</label>
                      <input
                        type="text"
                        name="charges"
                        value={formData.charges || ''}
                        onChange={handleFormChange}
                        placeholder="Charges"
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button onClick={handleSaveStudio} className="btn btn-primary">
                      Save
                    </button>
                    <button onClick={() => {
                      setShowForm(false);
                      setFormData({});
                      setEditingId(null);
                      setActiveTab('studios');
                    }} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Feedback Section */}
          {activeTab === 'feedback' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>📊 Feedback Management System</h2>
                <p style={{ color: '#666', marginTop: '10px', position: 'relative', left: '-20px' }}>
                  Complete feedback system showing Student→Teacher and Teacher→Student interactions
                </p>
              </div>

              {/* Student Feedback About Teachers */}
              <div style={{ marginBottom: '3rem' }}>
                <h3 style={{
                  color: '#333',
                  marginBottom: '1.5rem',
                  borderBottom: '3px solid #4CAF50',
                  paddingBottom: '0.5rem',
                  fontSize: '1.3rem',
                  fontWeight: '600'
                }}>
                  📝 Student Feedback About Teachers
                </h3>

                <div className="feedback-table-container">
                  <table className="feedback-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Student Name</th>
                        <th>Teacher Name</th>
                        <th>Rating</th>
                        <th>Category</th>
                        <th>Comment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbacks
                        .filter(f => f.giverType === 'student' && f.recipientType === 'teacher')
                        .map(feedback => (
                          <tr key={feedback.id}>
                            <td>{new Date(feedback.date).toLocaleDateString()}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: '#e3f2fd',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px'
                                }}>
                                  👤
                                </div>
                                <span style={{ fontWeight: '500' }}>{feedback.giverName}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: '#f3e5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px'
                                }}>
                                  👨‍🏫
                                </div>
                                <span style={{ fontWeight: '500' }}>{feedback.recipientName}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ color: '#ffc107', fontSize: '16px' }}>
                                  {'⭐'.repeat(feedback.rating)}
                                </span>
                                <span style={{ color: '#666', fontSize: '12px' }}>({feedback.rating}/5)</span>
                              </div>
                            </td>
                            <td>
                              <span style={{
                                background: '#e8f5e8',
                                color: '#2e7d32',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {feedback.category}
                              </span>
                            </td>
                            <td style={{ maxWidth: '300px' }}>
                              <p style={{
                                margin: '0',
                                fontSize: '14px',
                                lineHeight: '1.4',
                                color: '#555'
                              }}>
                                {feedback.comment}
                              </p>
                            </td>
                            <td>
                              <button
                                style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }}
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this feedback?')) {
                                    try {
                                      await apiCall(`/feedback/${feedback.id || feedback._id}`, 'DELETE');
                                      setFeedbacks(feedbacks.filter(f => (f.id || f._id) !== (feedback.id || feedback._id)));
                                    } catch (err) {
                                      alert("Failed to delete feedback: " + err.message);
                                    }
                                  }
                                }}
                                onMouseOver={(e) => e.target.style.background = '#c82333'}
                                onMouseOut={(e) => e.target.style.background = '#dc3545'}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {feedbacks.filter(f => f.giverType === 'student' && f.recipientType === 'teacher').length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#999',
                      fontSize: '16px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📝</div>
                      <p>No student feedback about teachers yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Teacher Feedback About Students */}
              <div>
                <h3 style={{
                  color: '#333',
                  marginBottom: '1.5rem',
                  borderBottom: '3px solid #2196F3',
                  paddingBottom: '0.5rem',
                  fontSize: '1.3rem',
                  fontWeight: '600'
                }}>
                  👨‍🏫 Teacher Feedback About Students
                </h3>

                <div className="feedback-table-container">
                  <table className="feedback-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Teacher Name</th>
                        <th>Student Name</th>
                        <th>Rating</th>
                        <th>Category</th>
                        <th>Comment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbacks
                        .filter(f => f.giverType === 'teacher' && f.recipientType === 'student')
                        .map(feedback => (
                          <tr key={feedback.id}>
                            <td>{new Date(feedback.date).toLocaleDateString()}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: '#f3e5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px'
                                }}>
                                  👨‍🏫
                                </div>
                                <span style={{ fontWeight: '500' }}>{feedback.giverName}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: '#e3f2fd',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px'
                                }}>
                                  👤
                                </div>
                                <span style={{ fontWeight: '500' }}>{feedback.recipientName}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ color: '#ffc107', fontSize: '16px' }}>
                                  {'⭐'.repeat(feedback.rating)}
                                </span>
                                <span style={{ color: '#666', fontSize: '12px' }}>({feedback.rating}/5)</span>
                              </div>
                            </td>
                            <td>
                              <span style={{
                                background: '#e3f2fd',
                                color: '#1565c0',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {feedback.category}
                              </span>
                            </td>
                            <td style={{ maxWidth: '300px' }}>
                              <p style={{
                                margin: '0',
                                fontSize: '14px',
                                lineHeight: '1.4',
                                color: '#555'
                              }}>
                                {feedback.comment}
                              </p>
                            </td>
                            <td>
                              <button
                                style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }}
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this feedback?')) {
                                    try {
                                      await apiCall(`/feedback/${feedback.id || feedback._id}`, 'DELETE');
                                      setFeedbacks(feedbacks.filter(f => (f.id || f._id) !== (feedback.id || feedback._id)));
                                    } catch (err) {
                                      alert("Failed to delete feedback: " + err.message);
                                    }
                                  }
                                }}
                                onMouseOver={(e) => e.target.style.background = '#c82333'}
                                onMouseOut={(e) => e.target.style.background = '#dc3545'}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {feedbacks.filter(f => f.giverType === 'teacher' && f.recipientType === 'student').length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#999',
                      fontSize: '16px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '1rem' }}>👨‍🏫</div>
                      <p>No teacher feedback about students yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary Statistics */}
              <div style={{
                marginTop: '3rem',
                padding: '2rem',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e0e0e0'
              }}>
                <h4 style={{
                  color: '#333',
                  marginBottom: '1rem',
                  textAlign: 'center',
                  fontSize: '1.2rem'
                }}>
                  📊 Feedback Summary
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50' }}>
                      {feedbacks.filter(f => f.giverType === 'student').length}
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Total Student Feedbacks</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3' }}>
                      {feedbacks.filter(f => f.giverType === 'teacher').length}
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Total Teacher Feedbacks</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FF9800' }}>
                      {feedbacks.length > 0 ? (feedbacks.reduce((sum, f) => sum + f.rating, 0) / feedbacks.length).toFixed(1) : '0'}
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Average Rating</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9C27B0' }}>
                      {new Set(feedbacks.map(f => f.giverId)).size}
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Active Feedback Givers</div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Bookings Section */}
          {activeTab === 'bookings' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>📅 Booking Submissions</h2>
              </div>

              {bookingSubmissions.length > 0 ? (
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Address</th>
                        <th>Class</th>
                        <th>Time</th>
                        <th>Submitted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingSubmissions.map((booking) => {
                        const bookingId = booking._id || booking.id;
                        return (
                          <tr key={bookingId}>
                            <td><strong>{booking.name || 'N/A'}</strong></td>
                            <td>{booking.email || 'N/A'}</td>
                            {/* <td><a href={`tel:${booking.phone}`} style={{ textDecoration: 'none' }}>{booking.phone || 'N/A'}</a></td> */}
                            <td>{booking.phone || 'N/A'}</td>
                            <td>{booking.city || 'N/A'}</td>
                            <td>{booking.state || 'N/A'}</td>
                            <td style={{ maxWidth: '300px', minWidth: '250px' }}>
                              <div style={{
                                maxHeight: '120px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                padding: '8px',
                                fontSize: '13px',
                                lineHeight: '1.4',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#f9f9f9',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word'
                              }} title={booking.address}>
                                {booking.address || 'N/A'}
                              </div>
                            </td>
                            <td><span className="badge badge-info">{booking.selectedClass || 'N/A'}</span></td>
                            <td>{booking.preferredTime || 'N/A'}</td>
                            <td>{booking.submittedAt ? new Date(booking.submittedAt).toLocaleDateString() : new Date(booking.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  onClick={() => {
                                    setFormData(booking);
                                    setEditingId(bookingId);
                                    setShowForm(true);
                                  }}
                                  className="btn btn-sm btn-primary"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteBooking(bookingId)}
                                  className="btn btn-sm btn-danger"
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                  <p>No booking submissions yet</p>
                </div>
              )}

              {/* Edit Booking Form */}
              {showForm && activeTab === 'bookings' && editingId && (
                <div className="form-card card" style={{ marginTop: '2rem' }}>
                  <div className="card-header">
                    <h3>Edit Booking</h3>
                  </div>
                  <div className="card-content">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleFormChange}
                          placeholder="Name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleFormChange}
                          placeholder="Email"
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone || ''}
                          onChange={handleFormChange}
                          placeholder="Phone"
                        />
                      </div>
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city || ''}
                          onChange={handleFormChange}
                          placeholder="City"
                        />
                      </div>
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state || ''}
                          onChange={handleFormChange}
                          placeholder="State"
                        />
                      </div>
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address || ''}
                          onChange={handleFormChange}
                          placeholder="Address"
                        />
                      </div>
                      <div className="form-group">
                        <label>Class</label>
                        <input
                          type="text"
                          name="selectedClass"
                          value={formData.selectedClass || ''}
                          onChange={handleFormChange}
                          placeholder="Class"
                        />
                      </div>
                      <div className="form-group">
                        <label>Preferred Time</label>
                        <input
                          type="text"
                          name="preferredTime"
                          value={formData.preferredTime || ''}
                          onChange={handleFormChange}
                          placeholder="Time"
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiCall(`/bookings/${editingId}`, 'PUT', formData);
                            alert('✅ Booking updated successfully!');

                            // Refresh list
                            const bookingsData = await apiCall('/bookings');
                            setBookingSubmissions(bookingsData);

                            setShowForm(false);
                            setEditingId(null);
                            setFormData({});
                          } catch (err) {
                            alert('Failed to update booking: ' + err.message);
                          }
                        }}
                        className="btn btn-primary"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => {
                          setShowForm(false);
                          setEditingId(null);
                          setFormData({});
                        }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Teacher Applications Section */}
          {activeTab === 'applications' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>🎓 Teacher Applications</h2>
              </div>

              {teacherApplications.length > 0 ? (
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Experience</th>
                        <th>Certifications</th>
                        <th>Certificate</th>
                        <th>Applied</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teacherApplications.map((app) => (
                        <tr key={app._id || app.id}>
                          <td><strong>{app.name}</strong></td>
                          <td>{app.email}</td>
                          {/* <td><a href={`tel:${app.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{app.phone}</a></td> */}
                          <td> {app.phone}</td>
                          <td>{app.experience}</td>
                          <td>{app.specialization || app.certifications || 'N/A'}</td>
                          <td>
                            {app.resume || app.certificateFile ? (
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => {
                                  // Use resume field prioritized as per controller
                                  const fileUrl = app.resume || app.certificateFile;
                                  if (fileUrl) {
                                    window.open(fileUrl.startsWith('http') ? fileUrl : `${API_URL.replace('/api', '')}/${fileUrl}`, '_blank');
                                  }
                                }}
                                title="View certificate"
                              >
                                📄 View
                              </button>
                            ) : (
                              <span style={{ color: '#999' }}>No certificate</span>
                            )}
                          </td>
                          <td>{new Date(app.submittedAt || app.createdAt).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge ${app.status === 'approved' ? 'badge-success' : (app.status === 'rejected' ? 'badge-danger' : 'badge-warning')}`}>
                              {app.status || 'pending'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => {
                                  setFormData(app);
                                  setEditingId(app._id || app.id);
                                  setShowForm(true);
                                }}
                                className="btn btn-sm btn-primary"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTeacherApp(app._id || app.id)}
                                className="btn btn-sm btn-danger"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                  <p>No teacher applications yet</p>
                </div>
              )}

              {/* Edit Teacher Application Form */}
              {showForm && activeTab === 'applications' && editingId && (
                <div className="form-card card" style={{ marginTop: '2rem' }}>
                  <div className="card-header">
                    <h3>Edit Teacher Application</h3>
                  </div>
                  <div className="card-content">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleFormChange}
                          placeholder="Name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleFormChange}
                          placeholder="Email"
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone || ''}
                          onChange={handleFormChange}
                          placeholder="Phone"
                        />
                      </div>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Experience</label>
                        <textarea
                          name="experience"
                          value={formData.experience || ''}
                          onChange={handleFormChange}
                          placeholder="Experience"
                          rows="3"
                        />
                      </div>
                      <div className="form-group">
                        <label>Specialization / Certifications</label>
                        <textarea
                          name="specialization"
                          value={formData.specialization || ''}
                          onChange={handleFormChange}
                          placeholder="Specialization/Certifications"
                          rows="3"
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          name="status"
                          value={formData.status || 'pending'}
                          onChange={handleFormChange}
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea
                          name="message"
                          value={formData.message || ''}
                          onChange={handleFormChange}
                          placeholder="Message"
                          rows="3"
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button
                        onClick={async () => {
                          try {
                            await apiCall(`/teacher-applications/${editingId}`, 'PUT', formData);
                            const apps = await apiCall('/teacher-applications');
                            setTeacherApplications(apps);
                            setShowForm(false);
                            setEditingId(null);
                            setFormData({});
                            alert('✅ Application updated successfully!');
                          } catch (err) {
                            alert('Failed to update application: ' + err.message);
                          }
                        }}
                        className="btn btn-primary"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => {
                          setShowForm(false);
                          setEditingId(null);
                          setFormData({});
                        }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Yoga Trainers Management Section */}
          {activeTab === 'trainers' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Yoga Trainers Management</h2>
                <button onClick={() => {
                  setShowForm(true);
                  setEditingId(null);
                  setFormData({});
                }} className="btn btn-primary">
                  ➕ Add New Trainer
                </button>
              </div>

              {/* Add/Edit Trainer Form */}
              {/* Add/Edit Trainer Form */}
              {showForm && (
                <div className="form-container">
                  <h3>{editingId ? 'Edit Trainer' : 'Add New Trainer'}</h3>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleTrainerSubmit();
                  }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Trainer Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name || ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Enter trainer name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Photo</label>
                        <input
                          type="file"
                          name="photo"
                          onChange={handleFormChange}
                          accept="image/*"
                        />
                        {formData.photo && !(formData.photo instanceof File) && (
                          <div style={{ fontSize: '12px', marginTop: '5px' }}>Current: {formData.photo.split('/').pop()}</div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Specialization *</label>
                        <input
                          type="text"
                          required
                          value={formData.specialization || ''}
                          onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                          placeholder="e.g., Hatha Yoga, Vinyasa"
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone *</label>
                        <input
                          type="tel"
                          required
                          value={formData.phone || ''}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91-XXXXXXXXXX"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          required
                          value={formData.email || ''}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="trainer@example.com"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>State *</label>
                        <select
                          required
                          value={formData.state || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, state: e.target.value, city: '' });
                          }}
                        >
                          <option value="">Select State</option>
                          <option>Uttar Pradesh</option>
                          <option>Delhi</option>
                          <option>Rajasthan</option>
                          <option>Maharashtra</option>
                          <option>Punjab</option>
                          <option>West Bengal</option>
                          <option>Karnataka</option>
                          <option>Tamil Nadu</option>
                          <option>Gujarat</option>
                          <option>Haryana</option>
                          <option>Madhya Pradesh</option>
                          <option>Telangana</option>
                          <option>Andhra Pradesh</option>
                          <option>Himachal Pradesh</option>
                          <option>Uttarakhand</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>City *</label>
                        <input
                          type="text"
                          required
                          value={formData.city || ''}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="Enter city name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Pin Code *</label>
                        <input
                          type="text"
                          required
                          value={formData.pincode || ''}
                          onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                          placeholder="e.g., 282001"
                          maxLength="6"
                        />
                      </div>
                      <div className="form-group">
                        <label>Experience *</label>
                        <input
                          type="text"
                          required
                          value={formData.experience || ''}
                          onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                          placeholder="e.g., 5+ Years"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.featured || false}
                            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                          />
                          {' '} Mark as Featured
                        </label>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">
                        {editingId ? 'Update Trainer' : 'Add Trainer'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setEditingId(null);
                          setFormData({});
                        }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Trainers List */}
              <div className="trainers-list-container">
                {/* Search Bar for Trainers */}
                <div className="trainers-search-section">
                  <input
                    type="text"
                    placeholder="🔍 Search trainers by name, phone, email, city, or state..."
                    value={trainerSearch}
                    onChange={(e) => setTrainerSearch(e.target.value)}
                    className="trainers-search-input"
                  />
                </div>

                <div className="table-wrapper">
                  <table className="trainers-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Specialization</th>
                        <th>Location</th>
                        <th>City</th>
                        <th>Pin Code</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Experience</th>
                        <th>Featured</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allTrainers && allTrainers.length > 0 ? (
                        allTrainers
                          .filter(trainer =>
                            trainer.name.toLowerCase().includes(trainerSearch.toLowerCase()) ||
                            trainer.phone.toLowerCase().includes(trainerSearch.toLowerCase()) ||
                            trainer.email.toLowerCase().includes(trainerSearch.toLowerCase()) ||
                            trainer.city.toLowerCase().includes(trainerSearch.toLowerCase()) ||
                            trainer.state.toLowerCase().includes(trainerSearch.toLowerCase()) ||
                            trainer.specialization.toLowerCase().includes(trainerSearch.toLowerCase())
                          )
                          .map(trainer => (
                            <tr key={trainer._id || trainer.id}>
                              <td data-label="Name"><strong>{trainer.name}</strong></td>
                              <td data-label="Specialization">{trainer.specialization}</td>
                              <td data-label="Location">{trainer.state}</td>
                              <td data-label="City">{trainer.city}</td>
                              <td data-label="Pin Code">{trainer.pincode}</td>
                              <td data-label="Phone"><a href={`tel:${trainer.phone}`} style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}>{trainer.phone}</a></td>
                              <td data-label="Email"><span className="email-cell">{trainer.email}</span></td>
                              <td data-label="Experience">{trainer.experience}</td>
                              <td data-label="Featured" className="featured-cell">{trainer.featured ? '⭐' : '-'}</td>
                              <td data-label="Actions" className="actions-cell">
                                <button
                                  onClick={() => handleEditTrainer(trainer)}
                                  className="btn btn-small btn-primary"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteTrainer(trainer._id || trainer.id)}
                                  className="btn btn-small btn-danger"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                            {allTrainers.length === 0 ? 'No trainers added yet' : 'No trainers match your search'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {/* Contact Messages Section */}
          {activeTab === 'contact' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Contact Messages</h2>
              </div>
              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Subject</th>
                      <th>Message</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactMessages.map((msg, index) => (
                      <tr key={msg._id || index}>
                        <td><strong>{msg.name}</strong></td>
                        <td>{msg.email}</td>
                        <td>{msg.phone}</td>
                        <td>{msg.subject}</td>
                        <td><div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={msg.message}>{msg.message}</div></td>
                        <td>{new Date(msg.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {contactMessages.length === 0 && (
                      <tr><td colSpan="6" style={{textAlign: 'center', padding: '1rem'}}>No contact messages found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Partner Applications Section */}
          {activeTab === 'partner_apps' && (
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Partner Applications</h2>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {partnerApplications.map((app, index) => (
                  <div key={app._id || index} className="card" style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0 }}>{app.fullName} <span style={{fontSize: '12px', padding: '2px 8px', borderRadius: '12px', background: '#e0f2fe', color: '#0369a1', marginLeft: '8px'}}>{app.registrationType?.toUpperCase()}</span></h3>
                      <span className="text-muted">{new Date(app.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <p><strong>Email:</strong> {app.email}</p>
                        <p><strong>Phone:</strong> {app.mobile}</p>
                        <p><strong>Location:</strong> {app.city}, {app.state} {app.pinCode}</p>
                        {app.registrationType === 'trainer' && (
                          <>
                            <p><strong>Experience:</strong> {app.yearsOfExperience} years</p>
                            <p><strong>Specializations:</strong> {app.yogaSpecializations?.join(', ')}</p>
                            <p><strong>Certified:</strong> {app.isCertified} {app.certificationDetails && `(${app.certificationDetails})`}</p>
                          </>
                        )}
                        {app.registrationType === 'studio' && (
                          <>
                            <p><strong>Studio Name:</strong> {app.studioName}</p>
                            <p><strong>Owner:</strong> {app.ownerName}</p>
                            <p><strong>Trainers:</strong> {app.numberOfTrainers}</p>
                            <p><strong>Fee:</strong> {app.feeAmount} ({app.feeType})</p>
                          </>
                        )}
                      </div>
                      <div>
                        <p><strong>Why Join:</strong> {app.whyJoinYogaKarega}</p>
                        {app.websiteLink && <p><strong>Website:</strong> <a href={app.websiteLink} target="_blank" rel="noreferrer">Link</a></p>}
                        {app.instagramProfile && <p><strong>Instagram:</strong> <a href={app.instagramProfile} target="_blank" rel="noreferrer">Link</a></p>}
                      </div>
                    </div>
                  </div>
                ))}
                {partnerApplications.length === 0 && (
                  <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>No partner applications found.</div>
                )}
              </div>
            </section>
          )}

          {/* Gallery Management Section */}
          {activeTab === 'gallery' && (
            <AdminGalleryManagement />
          )}

          {/* Banner Management Section */}
          {activeTab === 'banners' && (
            <AdminBannerManagement />
          )}

          {/* Classes Management Section */}
          {activeTab === 'classes' && (
            <AdminClassesManagement />
          )}

          {/* Community Chat Management Section */}
          {activeTab === 'community_chat' && (
            <AdminCommunityManagement />
          )}
        </div>
      </main>

      {popup.open && (
        <div className="modal-overlay" onClick={closePopup}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{popup.title}</h3>
              <button className="modal-close" onClick={closePopup}>✕</button>
            </div>
            <div className="modal-body">
              <p>{popup.message}</p>
              <div className="modal-actions">
                <button className="btn btn-primary" type="button" onClick={closePopup}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
